import React, {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { io } from 'socket.io-client';
import {X} from 'lucide-react';
import {SwapMainAppContext} from '../../context/SwapMainAppContext';
import chatStyles from './LiveChatModule.module.css';
import panelStyles from './TeleOpRightPanel.module.css';
import arrowIcon from '../../static_files/arrow-back-double.svg';
import {useIsMobile} from '../../utils/utils';
import Leaderboard from './Leaderboard';
import LiveChat from './LiveChat';
import TeleOpQueueBody from './TeleOpQueueBody';
import ControlsPanel from './ControlsPanel';
import {PRISMAX_BACKEND_Control_URL} from "../../configs/config";

// Initialize the socket outside of the component to persist across renders
const socket = io(PRISMAX_BACKEND_Control_URL, {
    autoConnect: false,
    transports: ['websocket'],
    upgrade: false
});

export default function TeleOpRightPanel({
                                            robotId,
                                            selectedRobot,
                                            defaultOpen = true,
                                            leftPanel = 'leaderboard', // 'leaderboard' | 'queue' | 'controls'
                                            userId,
                                            gatewayToken,
                                            setIsTeleOpMode,
                                            sessionExpired = false,
                                            setSessionExpired = () => {},
                                            onControlDataReceived = () => {},
                                            globalLive = false, // Flag to set custom styles for the GlobalLive page
                                             ...leftProps
                                         }) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [activeTab, setActiveTab] = useState(leftPanel);
    const isMobile = useIsMobile(1000);
    const shouldShowMonadLeaderboard = false;
    const { showNotification, setIsQueueAttentionActive } = useContext(SwapMainAppContext);
    const tabsTrackRef = useRef(null);
    const leftTabRef = useRef(null);
    const liveChatTabRef = useRef(null);
    const monadTabRef = useRef(null);
    const [thumbStyle, setThumbStyle] = useState({ width: 0, translateX: 0 });
    const [updateLabel, setUpdateLabel] = useState(() => buildUtcNoonUpdateLabel());

    // Robot queue state
    const [queueList, setQueueList] = useState([]);
    const [queuePosition, setQueuePosition] = useState(null);
    const [isInQueue, setIsInQueue] = useState(false);
    const [isQueueSocketConnected, setIsQueueSocketConnected] = useState(false);
    const hasActivatedRef = useRef(false);
    // Robot queue notification state (used to avoid re-sending browser notifications for the same position because of fast track)
    const hasSentFirstNotificationRef = useRef(false);
    const hasSentSecondNotificationRef = useRef(false);
   
    // Maintain a countdown of the remaining time for the active user in the queue
    const [activeUserRemainingTime, setActiveUserRemainingTime] = useState(0);
    const countdownIntervalRef = useRef(null);
    const activeUserIdRef = useRef(null);

    useEffect(() => {
        return () => {
            setIsQueueAttentionActive(false);
        };
    }, [setIsQueueAttentionActive]);
    
    // Socket.IO robot queue setup for establishing the connection
    useEffect(() => {
        // Do not open the queue socket connection if the required auth params are missing
        if (!robotId || !gatewayToken || !userId) {
            return;
        }
      
        // Connect with authentication data
        socket.auth = {
            robotId,
            token: gatewayToken,
            userId: userId
        };
        socket.connect();

        // Set up the event listeners
        socket.on('connection_success', (data) => {
            setIsQueueSocketConnected(true);
            console.log('Queue connection successful:', data);
        });

        socket.on('disconnect', (reason) => {
            // Alert the user only if the server rejected the connection request 
            if (reason === 'io server disconnect') {
                showNotification('error', 'Failed to retrieve queue status. Please try again.');
            }
            console.log('Queue disconnection: ', reason)
            setIsQueueSocketConnected(false);
        });

        // Cleanup on unmount
        return () => {
            socket.disconnect();
            socket.off('connection_success');
            socket.off('disconnect');
        };
    }, [gatewayToken, robotId, userId]);

    const startCountdown = (initialTime) => {
        // Stop any existing countdown
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
        }
        
        setActiveUserRemainingTime(initialTime);
        // Update remaining time every second
        countdownIntervalRef.current = setInterval(() => {
            setActiveUserRemainingTime((prev) => {
                const newTime = Math.max(0, prev - 1);
                if (newTime <= 0) {
                    clearInterval(countdownIntervalRef.current);
                    countdownIntervalRef.current = null;
                }
                return newTime;
            });
        }, 1000);
    };

    const stopCountdown = () => {
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        setActiveUserRemainingTime(0);
    };

    // Socket.IO event handler for queue updates
    const handleQueueUpdate = async (data) => {
        // Filter for the selected robot
        if (data.robotId !== selectedRobot) return;
        // Update queue list
        setQueueList(data.queue || []);

        // Check if user is in the queue
        const userInQueue = (data.queue || []).find((item) => item.user_id === userId);
        if (userInQueue) {
            setIsInQueue(true);
            const position = (data.queue || []).indexOf(userInQueue) + 1;
            setQueuePosition(position);
            setIsQueueAttentionActive(position <= 3);

            // If user is at position 1 with active status, fetch control token
            if (position === 1 && userInQueue.status === 'active' && !hasActivatedRef.current && !sessionExpired) {
                try {
                    const response = await fetch(
                        `${PRISMAX_BACKEND_Control_URL}/queue/control-token?userId=${userId}&robotId=${selectedRobot}`,
                        {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${gatewayToken}`,
                        },
                        }
                    );

                    if (response.ok) {
                        const controlData = await response.json();
                        handleUserTurn(controlData);
                    } else {
                        console.error('Failed to fetch control token:', await response.text());
                    }
                } catch (error) {
                    console.error('Error fetching control token:', error);
                }
            } 
            // Trigger browser notifications to notify the user when their turn is close
            else if (position === 2 && !hasSentSecondNotificationRef.current) {
                showBrowserNotification(
                    "You're at position 2 in the queue!",
                    "You're up next! There is one user ahead of you. Please return to the app."
                )
                hasSentSecondNotificationRef.current = true;
            } else if (position === 6 && !hasSentFirstNotificationRef.current) {
                showBrowserNotification(
                    "You're at position 6 in the queue!",
                    "It's almost your turn! Please continue to monitor the queue status." 
                )
                hasSentFirstNotificationRef.current = true;
            }
        } else {
            setIsInQueue(false);
            setQueuePosition(null);
            hasActivatedRef.current = false;
            setIsQueueAttentionActive(false);
        }

        // Handle countdown logic for active user
        const newActiveUser = data.queue?.[0];
        if (newActiveUser && newActiveUser.status === 'active') {
            // Start a new countdown only if the userId is different
            if (activeUserIdRef.current !== newActiveUser.user_id) {
                activeUserIdRef.current = newActiveUser.user_id;
                const newRemainingTime = newActiveUser.remaining_time || 0;
                startCountdown(newRemainingTime);
            }
        } else {
            // Clear countdown if no active user
            activeUserIdRef.current = null;
            stopCountdown();
        }
    };

    const handleUserTurn = (controlData) => {
        if (controlData && controlData.control_token) {
            hasActivatedRef.current = true;
            onControlDataReceived({ ...controlData, robotId: selectedRobot });
            setIsTeleOpMode(true);
            setIsInQueue(false);
            setActiveTab(leftPanel);
            showNotification('success', "It's your turn! You now have 5 minutes to control the robot.");
            setIsQueueAttentionActive(false);
            hasSentFirstNotificationRef.current = false;
            hasSentSecondNotificationRef.current = false;
        }
    };


    // Socket.IO setup for the queue update event listener. It is defined separately to avoid extra reconnections for sessionExpired https://socket.io/how-to/use-with-react#disconnection
    useEffect(() => {
        socket.on('queue_update', handleQueueUpdate);

        return () => {
            socket.off('queue_update');
        };
    }, [gatewayToken, selectedRobot, userId, sessionExpired]);


    useEffect(() => {
        setActiveTab((current) => {
            if (current === 'livechat' || current === 'monad_leaderboard') {
                return current;
            }
            return leftPanel;
        });
    }, [leftPanel]);

    useEffect(() => {
        if (!shouldShowMonadLeaderboard && activeTab === 'monad_leaderboard') {
            setActiveTab(leftPanel);
        }
    }, [shouldShowMonadLeaderboard, activeTab, leftPanel]);

    const leftLabel = useMemo(() => {
        switch (leftPanel) {
            case 'queue':
                return 'Queue';
            case 'controls':
                return 'Controls';
            case 'leaderboard':
            default:
                return 'Leaderboard';
        }
    }, [leftPanel]);

    const updateThumbPosition = useCallback(() => {
        const trackEl = tabsTrackRef.current;
        const activeEl =
            activeTab === 'livechat'
                ? liveChatTabRef.current
                : activeTab === 'monad_leaderboard' && shouldShowMonadLeaderboard
                    ? monadTabRef.current
                    : leftTabRef.current;
        if (!trackEl || !activeEl) return;
        const trackRect = trackEl.getBoundingClientRect();
        const tabRect = activeEl.getBoundingClientRect();
        setThumbStyle({
            width: tabRect.width,
            translateX: tabRect.left - trackRect.left,
        });
    }, [activeTab, shouldShowMonadLeaderboard]);

    useLayoutEffect(() => {
        updateThumbPosition();
    }, [updateThumbPosition, leftPanel]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const handleResize = () => updateThumbPosition();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [updateThumbPosition]);

    useEffect(() => {
        const interval = setInterval(() => {
            setUpdateLabel(buildUtcNoonUpdateLabel());
        }, 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    if (!isOpen && !isMobile) {
        return (
            <button className={panelStyles.openBtnDesktop} onClick={() => setIsOpen(true)}>
                <img src={arrowIcon} alt="<<"/>
                {activeTab === 'livechat' ? 'Open Live Chat' : `Open ${leftLabel}`}
            </button>
        );
    }

    if (!isOpen) {
        return (
            <button className={panelStyles.openBtnMobile} onClick={() => setIsOpen(true)}>
                {activeTab === 'livechat' ? 'Open Live Chat' : `Open ${leftLabel}`}
            </button>
        );
    }

    return (
        <div className={`${chatStyles.queuePanel} ${globalLive && chatStyles.globalLiveQueuePanel}`}>
            <div className={chatStyles.header}>
                <div className={chatStyles.headerLeft}>
                    <div className={panelStyles.tabContainer}>
                        <div className={panelStyles.panelTabs} role="tablist" aria-label="Right Panel Tabs">
                            <button
                                ref={leftTabRef}
                                type="button"
                                role="tab"
                                aria-selected={activeTab === leftPanel}
                                className={`${panelStyles.tabBtn} ${activeTab === leftPanel ? panelStyles.activeTab : ''}`}
                                onClick={() => setActiveTab(leftPanel)}
                            >
                                {leftLabel}
                            </button>
                            <button
                                ref={liveChatTabRef}
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'livechat'}
                                className={`${panelStyles.tabBtn} ${activeTab === 'livechat' ? panelStyles.activeTab : ''}`}
                                onClick={() => setActiveTab('livechat')}
                            >
                                Live Chat
                            </button>
                            {shouldShowMonadLeaderboard && (
                                <button
                                    ref={monadTabRef}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === 'monad_leaderboard'}
                                    className={`${panelStyles.tabBtn} ${activeTab === 'monad_leaderboard' ? panelStyles.activeTab : ''}`}
                                    onClick={() => setActiveTab('monad_leaderboard')}
                                >
                                    Monad Leaderboard
                                </button>
                            )}
                        </div>
                    </div>

                </div>

                <button
                    className={chatStyles.closeBtn}
                    onClick={() => setIsOpen(false)}
                    aria-label="Close"
                >
                    <X size={20}/>
                </button>
            </div>
            <div className={panelStyles.tabsTrack} ref={tabsTrackRef}>
              <span
                  className={panelStyles.tabsThumb}
                  style={{
                      width: thumbStyle.width ? `${thumbStyle.width}px` : '0px',
                      transform: `translateX(${thumbStyle.translateX}px)`,
                  }}
              />
            </div>
            {(activeTab === 'leaderboard' || activeTab === 'monad_leaderboard') && (
                <div className={panelStyles.tabsUpdateNote}>Last Update: 12/25</div>
            )}
            <div className={chatStyles.content}>
                {activeTab === 'livechat' ? (
                    <LiveChat robotId={robotId}/>
                ) : activeTab === 'monad_leaderboard' && shouldShowMonadLeaderboard ? (
                    <Leaderboard leaderboardChain="monad" />
                ) : (
                    leftPanel === 'leaderboard' ? (
                        <Leaderboard/>
                    ) : leftPanel === 'queue' ? (
                        <TeleOpQueueBody
                            {...leftProps}
                            selectedRobot={selectedRobot}
                            robotId={robotId}
                            userId={userId}
                            gatewayToken={gatewayToken}
                            setSessionExpired={setSessionExpired}
                            // Queue props
                            queueList={queueList}
                            queuePosition={queuePosition}
                            isInQueue={isInQueue}
                            setIsInQueue={setIsInQueue}
                            setQueuePosition={setQueuePosition}
                            isQueueSocketConnected={isQueueSocketConnected}
                            hasActivatedRef={hasActivatedRef}
                            activeUserRemainingTime={activeUserRemainingTime}
                            hasSentFirstNotificationRef={hasSentFirstNotificationRef}
                            hasSentSecondNotificationRef={hasSentSecondNotificationRef}
                        />
                    ) : (
                        <ControlsPanel {...leftProps} embedded />
                    )
                )}
            </div>
        </div>
    );
}

function buildUtcNoonUpdateLabel(referenceDate = new Date()) {
    const latestNoonUtc = getLatestUtcNoonDate(referenceDate);
    return formatUpdateLabelFromDate(latestNoonUtc);
}

function getLatestUtcNoonDate(referenceDate = new Date()) {
    if (!(referenceDate instanceof Date) || Number.isNaN(referenceDate.getTime())) {
        return null;
    }
    const candidateMs = Date.UTC(
        referenceDate.getUTCFullYear(),
        referenceDate.getUTCMonth(),
        referenceDate.getUTCDate(),
        12,
        0,
        0,
        0
    );
    if (referenceDate.getTime() >= candidateMs) {
        return new Date(candidateMs);
    }
    return new Date(candidateMs - 24 * 60 * 60 * 1000);
}

function formatUpdateLabelFromDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return 'Update: --';
    }
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const normalizedHour = hours % 12 === 0 ? 12 : hours % 12;
    const minuteStr = String(minutes).padStart(2, '0');

    const formattedTime = `${normalizedHour}:${minuteStr}${suffix}`;
    return `Update: ${month}/${day} ${formattedTime}`;
}

// Function to show browser notification. Used for queue updates.
const showBrowserNotification = (title, body, duration = 12000) => {
    if ('Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification(title, {
            body: body,
            icon: '/favicon.svg', // display the PrismaX app icon
            badge: '/favicon.svg',
            tag: 'queue-update',
            requireInteraction: false
        });

        // Auto-close after the specified number of seconds
        setTimeout(() => {
            notification.close();
        }, duration);
    }
};
